import React, { useEffect, useState } from "react";
import { Alert, Button, Modal, Spinner } from "react-bootstrap";
import { apiGetOrderPaymentRecovery, apiReconcileOrderPayment } from "../../api";
import MerchantPaymentClaims from "./MerchantPaymentClaims";
import OrderProviderCalls from "../payments/OrderProviderCalls";

export default function PaymentRecoveryDialog({ orderId, onClose, onRecovered }) {
  const [review, setReview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [notice, setNotice] = useState("");

  useEffect(() => {
    let cancelled = false;
    setBusy(true);
    apiGetOrderPaymentRecovery(orderId).then(({ data }) => {
      if (!cancelled) setReview(data.data);
    }).catch(err => {
      if (!cancelled) setError(err.response?.data?.message || "Could not load the payment review.");
    }).finally(() => { if (!cancelled) setBusy(false); });
    return () => { cancelled = true; };
  }, [orderId]);

  const refresh = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await apiGetOrderPaymentRecovery(orderId);
      setReview(data.data); setError("");
    } catch (err) { setError(err.response?.data?.message || "Could not refresh the payment review."); }
    finally { setBusy(false); }
  };
  const reconcile = async () => {
    if (busy) return;
    setBusy(true); setError("");
    try {
      const { data } = await apiReconcileOrderPayment(orderId);
      if (data.data.reconciled) { setResult(data.data); onRecovered?.(); }
      else {
        setNotice(data.data.message);
        const refreshed = await apiGetOrderPaymentRecovery(orderId);
        setReview(refreshed.data.data);
      }
    } catch (err) { setError(err.response?.data?.message || "The provider status is uncertain. Do not collect another payment."); }
    finally { setBusy(false); }
  };
  return (
    <Modal show size="lg" onHide={busy ? undefined : onClose} backdrop={busy ? "static" : true}>
      <Modal.Header closeButton={!busy}><Modal.Title>Review order payment</Modal.Title></Modal.Header>
      <Modal.Body>
        {busy && !review && <Spinner animation="border" />}
        {error && <Alert variant="danger">{error}</Alert>}
        {notice && !result && <Alert variant="info">{notice}</Alert>}
        {review && <>
          <p>Order #{String(orderId).slice(-8)} · <strong>{review.currency} {Number(review.amount).toLocaleString()}</strong></p>
          <p>Current method: {review.paymentMethod} · Payment status: {review.paymentStatus}</p>
          {/* The provider's own reason and the line it charged. A failed
              mobile money charge is most often a wrong number, and the number
              the customer typed is the first thing to compare. */}
          {!!review.payerPhone && <p>Charged number: <strong>{review.payerPhone}</strong></p>}
          {!!review.paymentFailureReason && <Alert variant="warning">Provider's reason: {review.paymentFailureReason}</Alert>}
          <p>Check the provider’s record of the existing payment. This screen cannot request another charge or change the collection method.</p>
          {!!review.references?.length && <details className="mb-3"><summary>Saved payment references</summary>
            <ul>{review.references.map(r => <li key={`${r.rail}:${r.reference}`} style={{ overflowWrap: "anywhere" }}>{r.rail}: {r.reference}</li>)}</ul>
          </details>}
          <OrderProviderCalls orderId={orderId} />
          {review.blocked && <Alert variant="warning">{review.blocked.message}</Alert>}
          {result ? <Alert variant="success">{result.message}</Alert> : <Alert variant="info">
            Merchant-code recovery is not available yet. Do not ask the customer to send another payment until the earlier payment is resolved and approved merchant instructions are available.
          </Alert>}
          <MerchantPaymentClaims orderId={orderId} disabled={busy} onBusyChange={setBusy} />
        </>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" disabled={busy} onClick={onClose}>Close</Button>
        {!result && <Button variant="outline-primary" disabled={busy} onClick={refresh}>Refresh review</Button>}
        {!result && review && <Button variant="outline-primary" disabled={busy} onClick={reconcile}>Check provider payment</Button>}
      </Modal.Footer>
    </Modal>
  );
}
